import { useEffect, useState } from "react";
import { useFeedbackReview } from "../hooks/useFeedbackReview";
import { useToast } from "@/components/ui/toast";
import { errorMessage } from "@/lib/api";
import {
  PLAYER_RESPONSE_SIGNALS,
  PLAYER_RESPONSE_SIGNAL_CONFIG,
  type PlayerResponseSignal,
} from "../responseSignals";

/**
 * Three-chip player response affordance — PR 6 of the Feedback
 * Player UX redesign (Mockup Phase 6).
 *
 * Mounted on:
 *   - the unified Review card (under the meta line),
 *   - the focused player modal (between the body and the Mark
 *     reviewed footer).
 *
 * Persists by calling `markReviewed` (the existing
 * `POST /api/my-feedback/review` endpoint) with the structured
 * signal encoded into the `reflection` free-text field — see
 * `responseSignals.ts` for the encoding contract. No backend
 * migration needed for PR 6; a future column promotion can strip
 * the prefix on persist without touching this surface.
 *
 * State transitions:
 *   - "default" (no current signal) — three chips + an optional
 *     text field collapsed.
 *   - "responded" — collapses to a single "You · ⚡ I tried this"
 *     summary chip + optional note + "Edit response" button.
 *
 * The note persists on its own (blur, or the explicit "Save note"
 * button) rather than only riding along with a chip click. Clicking
 * a chip used to be the sole way to persist, and it also collapsed
 * the editor — so a player who tapped a chip and then typed lost the
 * text silently. `response_signal` is omitted from a note-only save,
 * which the endpoint reads as "preserve the stored signal" (see
 * `MarkCoachingReviewRequest`), so the two writes never clobber each
 * other.
 */

export type PlayerResponseTarget =
  | { kind: "note"; noteId: number }
  | { kind: "playlist"; playlistId: number }
  /** PR 7: clips now persist via the same endpoint once the
   * backend gained `clip_id` support on `coaching_reviews`. */
  | { kind: "clip"; clipId: number };

export type PlayerResponseChipsProps = {
  target: PlayerResponseTarget;
  /** Current signal from the bundle, or `null` if none. */
  currentSignal: PlayerResponseSignal | null;
  /** Current note text from the bundle (empty when none). */
  currentNote: string;
  /** ISO timestamp of when a coach marked this response seen, or
   * null/undefined when they have not. The player asked a question
   * into a void otherwise: a `need_help` response shows up on the
   * coach's board, gets acted on, and the player never learns anyone
   * read it. */
  acknowledgedAt?: string | null;
  /** Optional callback so the caller can react to a successful
   * update (e.g. close the modal, refresh local state). */
  onResponded?: () => void;
};

export function PlayerResponseChips({
  target,
  currentSignal,
  currentNote,
  acknowledgedAt,
  onResponded,
}: PlayerResponseChipsProps) {
  const review = useFeedbackReview();
  const toast = useToast();

  const [editing, setEditing] = useState(!currentSignal);
  const [selected, setSelected] = useState<PlayerResponseSignal | null>(
    currentSignal,
  );
  const [note, setNote] = useState(currentNote);
  const [noteOpen, setNoteOpen] = useState(false);
  /** Last note text we know the server holds. Drives the dirty check
   * so blur doesn't fire a pointless write, and so the "saved"
   * confirmation only shows for text that actually landed. */
  const [savedNote, setSavedNote] = useState(currentNote);
  const [noteSaved, setNoteSaved] = useState(false);

  // Re-sync from props when the surrounding bundle refreshes (e.g.
  // after a different surface persisted a response for the same id).
  useEffect(() => {
    setSelected(currentSignal);
    setNote(currentNote);
    setSavedNote(currentNote);
    setNoteSaved(false);
    setEditing(!currentSignal);
    setNoteOpen(!!currentNote);
  }, [currentSignal, currentNote, target.kind, idFor(target)]);

  function targetIds() {
    return target.kind === "note"
      ? { note_id: target.noteId }
      : target.kind === "playlist"
        ? { playlist_id: target.playlistId }
        : { clip_id: target.clipId };
  }

  const noteDirty = note.trim() !== savedNote.trim();

  async function submit(signal: PlayerResponseSignal) {
    setSelected(signal);
    try {
      // Phase B: send the signal as its own field + the note as a
      // prefix-free reflection (no more `[SIGNAL:*]` encoding).
      //
      // The note rides along only when the player actually changed it.
      // `""` is not "no note" to the endpoint — it explicitly CLEARS
      // the stored reflection (omitting preserves; see
      // `MarkCoachingReviewRequest`). Sending it unconditionally meant
      // opening "Add a note", typing nothing, and then tapping a chip
      // silently deleted a reflection the player had already saved.
      await review.markReviewed({
        ...targetIds(),
        response_signal: signal,
        ...(noteDirty ? { reflection: note.trim() } : {}),
      });
      setSavedNote(note.trim());
      setNoteSaved(false);
      toast.success("Sent to your coach.");
      setEditing(false);
      onResponded?.();
    } catch (err) {
      toast.error(errorMessage(err) || "Couldn't send your response.");
    }
  }

  /** Persist the note by itself. `response_signal` is deliberately
   * omitted so the endpoint preserves whatever signal is stored — a
   * note-only save must never clear an existing "Got it". */
  async function saveNote() {
    if (!noteDirty || review.isPending) return;
    try {
      await review.markReviewed({
        ...targetIds(),
        reflection: note.trim(),
      });
      setSavedNote(note.trim());
      setNoteSaved(true);
      onResponded?.();
    } catch (err) {
      toast.error(errorMessage(err) || "Couldn't save your note.");
    }
  }

  // The "edit response" entry from the responded state — open the
  // full chip + note editor again, pre-populated. `openNote` forces
  // the note field open so "Add a note" lands the player straight in
  // the textarea instead of making them hunt for the disclosure.
  function startEditing(openNote = false) {
    setEditing(true);
    setNoteOpen(openNote || !!(currentNote || savedNote));
  }

  // Fall back to the locally-selected signal: after a chip click the
  // bundle hasn't refetched yet, so `currentSignal` is still null and
  // keying the collapsed view off the prop alone left the player in
  // the editor with no way to reach "Add a note".
  const respondedSignal = currentSignal ?? selected;
  // Same for the note — show what we just persisted, not the stale prop.
  const respondedNote = currentNote || savedNote;

  if (!editing && respondedSignal) {
    const config = PLAYER_RESPONSE_SIGNAL_CONFIG[respondedSignal];
    return (
      <div
        className="flex flex-col gap-2 rounded-panel border border-accent/40 bg-accent/5 px-3 py-2"
        data-testid="player-response-chips"
        data-response-state="responded"
      >
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span
            className="text-xs font-display text-ink"
            data-testid="player-response-summary"
          >
            <span aria-hidden="true">{config.glyph}</span>{" "}
            <span>You said: {config.label}</span>
          </span>
          <button
            type="button"
            onClick={() => startEditing()}
            data-testid="player-response-edit"
            className="eyebrow rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Edit response
          </button>
        </div>
        {respondedNote ? (
          <p
            className="text-xs text-ink-muted"
            data-testid="player-response-note"
          >
            "{respondedNote}"
          </p>
        ) : (
          // Without this the only way to add a first note is "Edit
          // response", which reads as changing the chip — the exact
          // dead end that made players lose notes.
          <button
            type="button"
            onClick={() => startEditing(true)}
            data-testid="player-response-add-note"
            className="eyebrow self-start rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
          >
            Add a note (optional)
          </button>
        )}
        {acknowledgedAt ? (
          // The point of the whole ack path. A player who wrote
          // "Need help" has no other way to learn that anyone read
          // it — the coach acts in the Library, out of sight.
          <p
            className="text-xs text-accent"
            data-testid="player-response-acknowledged"
          >
            Coach saw this
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-2 rounded-panel border border-rule bg-surface px-3 py-2 shadow-rest"
      data-testid="player-response-chips"
      data-response-state={selected ? "selecting" : "default"}
    >
      <p className="eyebrow text-[0.6rem]">How'd it land?</p>
      <div
        className="flex flex-wrap gap-1.5"
        role="group"
        aria-label="Response signals"
      >
        {PLAYER_RESPONSE_SIGNALS.map((signal) => {
          const config = PLAYER_RESPONSE_SIGNAL_CONFIG[signal];
          const isActive = selected === signal;
          const isPending = review.isPending && isActive;
          return (
            <button
              key={signal}
              type="button"
              onClick={() => submit(signal)}
              disabled={review.isPending}
              aria-pressed={isActive}
              data-feedback-response-chip={signal}
              data-active={isActive}
              className={[
                "inline-flex items-center gap-1.5 rounded-pill px-3 py-1 text-xs font-display transition-colors disabled:opacity-50",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas",
                signal === "got_it"
                  ? isActive
                    ? "bg-good text-navy"
                    : "bg-good/10 text-good hover:bg-good/20"
                  : signal === "need_help"
                    ? isActive
                      ? "bg-flag text-navy"
                      : "bg-flag/10 text-flag hover:bg-flag/20"
                    : isActive
                      ? "bg-accent text-accent-ink"
                      : "bg-accent/10 text-ink hover:bg-accent/20",
              ].join(" ")}
            >
              <span aria-hidden="true">{config.glyph}</span>
              <span>{isPending ? "…" : config.label}</span>
            </button>
          );
        })}
      </div>
      {/* Controlled disclosure — avoids the native <details>/<summary>
       * triangle marker (we don't render any system chrome). The
       * caret + label is a single button toggling the textarea. */}
      <button
        type="button"
        onClick={() => setNoteOpen((open) => !open)}
        aria-expanded={noteOpen}
        data-testid="player-response-note-toggle"
        className="eyebrow self-start rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
      >
        {noteOpen ? "Hide note" : "Add a note (optional)"}
      </button>
      {noteOpen ? (
        <div className="flex flex-col gap-1.5">
          <textarea
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
              setNoteSaved(false);
            }}
            onBlur={saveNote}
            placeholder="Add a quick note for your coach…"
            maxLength={1000}
            rows={2}
            data-testid="player-response-note-input"
            className="w-full rounded-control border border-rule bg-surface px-2 py-1 text-sm text-ink resize-y focus:outline-none focus:ring-2 focus:ring-accent"
          />
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <span
              className="text-[0.7rem] text-good"
              data-testid="player-response-note-saved"
              aria-live="polite"
            >
              {noteSaved && !noteDirty ? "✓ Note saved" : ""}
            </span>
            {noteDirty ? (
              <button
                type="button"
                onClick={saveNote}
                disabled={review.isPending}
                data-testid="player-response-note-save"
                className="eyebrow rounded-control text-[0.7rem] text-ink-muted transition-colors hover:text-accent disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-canvas"
              >
                Save note
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function idFor(t: PlayerResponseTarget): number {
  if (t.kind === "note") return t.noteId;
  if (t.kind === "playlist") return t.playlistId;
  return t.clipId;
}
